import React from "react";
import AppModal from "../../core/components/app-modal";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import useModalWithData from "@common/hooks/use-modal-with-data";
import {animated} from "@react-spring/web";
import {useModalAnimation} from "../../core/animations";
import {ReactComponent as CrossIcon} from "@assets/icons/cross.svg";
import AppButton from "@common/components/app-button";
import {convertLexicalJsonToHtml} from "../../articles/helpers/lexical-to-html";
import {sanitizeHTML} from "../../articles/helpers/safe-html-renderer";
import {convertLexicalJsonToPlainText} from "../../articles/helpers/lexical-to-plain-text";

export interface VisitRequirementPreviewModalData {
    required: string;
    optional: string;
}

const VisitRequirementPreviewModal = () => {
    const {data, hideModal, isOpenModal} = useModalWithData<VisitRequirementPreviewModalData>(ModalIds.VISIT_REQUIREMENT_PREVIEW);
    const {springProps, closeModal} = useModalAnimation(!!isOpenModal(), 'medium');

    const handleClose = () => closeModal( () => hideModal());

    const toHtml = (value: string): string => {
        if (!value) return '';
        try {
            if (value.startsWith('{') || value.startsWith('[')) {
                return sanitizeHTML(convertLexicalJsonToHtml(value));
            }
        } catch {}
        return sanitizeHTML(value);
    };

    const isRichTextEmpty = (value?: string): boolean => {
        if (!value) return true;
        try {
            if (value.startsWith('{') || value.startsWith('[')) {
                return convertLexicalJsonToPlainText(value).trim().length === 0;
            }
        } catch {}
        return value.trim().length === 0;
    };

    const hasRequired = !isRichTextEmpty(data?.required);
    const hasOptional = !isRichTextEmpty(data?.optional);
    const hasAny = hasRequired || hasOptional;

    if (!data) return null;

    return (
        <AppModal
            id={ModalIds.VISIT_REQUIREMENT_PREVIEW}
            zIndex={40}
            forceFixed={true}
            classModal="max-w-lg">
            <animated.div
                style={springProps}
                className="h-full flex flex-col bg-surface-level-1 rounded-2xl min-w-full md:min-w-[648px] md:h-[80svh] max-h-[80svh] md:max-h-[600px]"
            >
                <div className="flex items-center justify-between p-4 border-b border-gray-200 max-h-[72px]">
                    <span className="text-lg font-semibold">Podgląd widgetu</span>
                    <AppButton
                        onClick={handleClose}
                        className="border-0 rounded-full !p-4 text-gray-600 hover:text-gray-800 hover:bg-on-surface/5 transition-colors"
                    >
                        <CrossIcon className="w-[15px] h-[15px]"/>
                    </AppButton>
                </div>

                {!hasAny ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                        <p className="text-sm text-on-surface/50">
                            Brak informacji o przygotowaniu do wizyty.
                        </p>
                    </div>
                ): (
                    <div className="px-6 py-6 flex flex-col gap-4 overflow-y-auto">
                        <div className="flex items-start gap-3">
                            <div className="w-7 min-w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                                <span className="text-on-primary text-xs font-semibold">i</span>
                            </div>
                            <div className="flex flex-col gap-1">
                                <h2 className="text-lg font-semibold text-on-surface my-0">
                                    Jak przygotować się do wizyty
                                </h2>
                                <p className="text-[15px] leading-6 text-on-surface/80">
                                    Ta krótka lista pomoże Ci zabrać wszystko, co najważniejsze na wizytę u specjalisty.
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            {data.required && (
                                <div className="bg-[#F0F7F7] rounded-xl p-4 flex flex-col gap-3">
                                    <p className="text-[14px] font-semibold text-primary uppercase tracking-wide">
                                        1. Wymagane
                                    </p>
                                    <div
                                        className="[&_*]:text-on-surface [&_*]:text-[15px] [&_ul]:mb-0 [&_ol]:mb-0 [&_ul]:space-y-0 [&_ol]:space-y-0 [&_ul]:pl-4 [&_ol]:pl-4 [&_ul]:list-outside [&_ol]:list-outside"
                                        dangerouslySetInnerHTML={{__html: toHtml(data.required)}}
                                    />
                                </div>
                            )}
                            {data.optional && (
                                <div className="rounded-xl p-4 flex flex-col gap-3">
                                    <p className="text-[14px] font-semibold text-on-surface uppercase tracking-wide">
                                        2. Opcjonalne
                                    </p>
                                    <div
                                        className="[&_*]:text-on-surface [&_*]:text-[15px] [&_ul]:pl-4 [&_ol]:pl-4 [&_ul]:list-outside [&_ol]:list-outside"
                                        dangerouslySetInnerHTML={{__html: toHtml(data.optional)}}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-end gap-2 h-[72px] min-h-[72px] rounded-b-2xl border-t border-outline-mild px-6">
                    <AppButton
                        onClick={handleClose}
                        className="bg-transparent hover:bg-on-surface/5 max-h-[40px] h-[40px] !px-6 !pt-2 border-[2px] !border-on-surface font-semibold text-base !rounded-full"
                        label="Zamknij"
                    />
                </div>
            </animated.div>
        </AppModal>
    );
};

export default VisitRequirementPreviewModal;
